import { useCallback, useEffect, useRef, useState } from "react";
import {
  FiMessageSquare,
  FiPhone,
  FiVideo,
  FiSettings,
  FiDollarSign,
  FiLoader,
  FiX,
  FiMic,
  FiMicOff,
  FiMonitor,
  FiCornerUpLeft,
  FiDownload,
  FiDisc,
} from "react-icons/fi";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useLocalParticipant,
  useTracks,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import MintSpaceAppWindow from "../components/MintSpaceAppWindow";
import EmptyState from "../components/ds/EmptyState";
import StatCell from "../components/ds/StatCell";
import ViewTabs from "../components/ds/ViewTabs";
import ConsultingMessageBody from "../components/ConsultingMessageBody";
import ConsultingChatComposer from "../components/ConsultingChatComposer";
import ConsultingOfferingsPanel from "../components/ConsultingOfferingsPanel";
import { upsertConsultingMessages } from "../utils/consultingMessageState";
import ConsultingOfferingForm from "../components/ConsultingOfferingForm";
import ConsultingCalendarPanel from "../components/ConsultingCalendarPanel";
import ConsultingUpcomingBanner from "../components/ConsultingUpcomingBanner";
import { LIVEKIT_URL } from "../config/env";
import {
  listConsultingEngagements,
  listConsultingMessages,
  sendConsultingMessage,
  closeConsultingEngagement,
  getConsultingCallToken,
  getConsultingEngagement,
  getConsultingStats,
  uploadConsultingMedia,
  listConsultingRecordings,
  setConsultingRecordingConsent,
  startConsultingRecording,
  stopConsultingRecording,
  downloadConsultingRecording,
  publishConsultingRecordingProduct,
} from "../api/mintSpaceConsulting";

function CallRecordingControls({
  isDark,
  mintSpaceId,
  engagementId,
  recording,
  egressConfigured,
  onRefresh,
  setToast,
  t,
}) {
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = recording?.status === "recording";
  const both = recording?.bothConsented;

  const toggleConsent = async () => {
    const next = !consent;
    setConsent(next);
    setBusy(true);
    try {
      await setConsultingRecordingConsent(mintSpaceId, engagementId, next);
      await onRefresh?.();
    } catch (err) {
      setConsent(!next);
      setToast?.(err.response?.data?.message || err.message || "Consent failed");
    } finally {
      setBusy(false);
    }
  };

  const startRec = async () => {
    setBusy(true);
    try {
      await startConsultingRecording(mintSpaceId, engagementId);
      await onRefresh?.();
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Could not start recording");
    } finally {
      setBusy(false);
    }
  };

  const stopRec = async () => {
    setBusy(true);
    try {
      await stopConsultingRecording(mintSpaceId, engagementId);
      await onRefresh?.();
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Could not stop recording");
    } finally {
      setBusy(false);
    }
  };

  if (!egressConfigured) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-white/10 px-3 py-2 text-caption-1 text-white/90">
      <label className="flex cursor-pointer items-center gap-1.5">
        <input type="checkbox" checked={consent} disabled={busy} onChange={toggleConsent} />
        {t("consulting.recordConsent") || "I agree to record this session"}
      </label>
      {both && !active && (
        <button
          type="button"
          disabled={busy}
          onClick={startRec}
          className="inline-flex items-center gap-1 rounded bg-red-600 px-2 py-1 font-semibold text-white"
        >
          <FiDisc className="h-3 w-3" />
          {t("consulting.recordStart") || "Record"}
        </button>
      )}
      {active && (
        <button
          type="button"
          disabled={busy}
          onClick={stopRec}
          className="inline-flex items-center gap-1 rounded bg-white/20 px-2 py-1 font-semibold"
        >
          {t("consulting.recordStop") || "Stop"}
        </button>
      )}
      {recording?.status === "recording" && (
        <span className="animate-pulse text-status-danger">{t("consulting.recordingLive") || "REC"}</span>
      )}
    </div>
  );
}

function CallControls({ isDark, onLeave }) {
  const { localParticipant } = useLocalParticipant();
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const btn = "bg-fill-secondary text-label-primary";

  const toggleCam = async () => {
    const next = !camOn;
    await localParticipant.setCameraEnabled(next);
    setCamOn(next);
  };
  const toggleMic = async () => {
    const next = !micOn;
    await localParticipant.setMicrophoneEnabled(next);
    setMicOn(next);
  };

  return (
    <div className="flex shrink-0 items-center justify-center gap-2 border-t border-white/10 bg-black/80 px-3 py-2">
      <button type="button" className={`rounded-radius-xs p-2 ${btn}`} onClick={toggleCam}>
        {camOn ? <FiVideo className="h-4 w-4" /> : <FiVideo className="h-4 w-4 opacity-50" />}
      </button>
      <button type="button" className={`rounded-radius-xs p-2 ${btn}`} onClick={toggleMic}>
        {micOn ? <FiMic className="h-4 w-4" /> : <FiMicOff className="h-4 w-4" />}
      </button>
      <button
        type="button"
        className={`rounded-radius-xs p-2 ${btn}`}
        onClick={() => localParticipant.setScreenShareEnabled(true)}
      >
        <FiMonitor className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onLeave}
        className="rounded-radius-xs bg-red-500 px-3 py-2 text-footnote-semibold text-white"
      >
        Leave
      </button>
    </div>
  );
}

function CallVideoGrid() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );
  const tiles = tracks.filter((t) => t.publication?.track || t.participant?.isLocal);
  if (tiles.length === 0) {
    return <div className="flex flex-1 items-center justify-center text-subheadline text-slate-400">Connecting…</div>;
  }
  return (
    <div className={`grid h-full min-h-0 flex-1 gap-1 p-1 ${tiles.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
      {tiles.slice(0, 4).map((tr) => (
        <VideoTrack key={tr.publication?.trackSid || tr.participant.identity} trackRef={tr} className="h-full w-full rounded-radius-xs bg-black object-cover" />
      ))}
    </div>
  );
}

export default function MintSpaceConsultingPage({
  isDark,
  setToast,
  mintSpace,
  isAdmin = false,
  appInstanceId,
  onMintSpaceSaved,
  onOpenAffiliate,
  onOpenProperties,
  initialEngagementId,
  initialHubTab,
  initialOpenOffering,
  onClearOfferingUrl,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { socket } = useSocket();
  const userId = user?._id || user?.id;
  const mintSpaceId = mintSpace?._id;

  const [loading, setLoading] = useState(true);
  const [engagements, setEngagements] = useState([]);
  const activeSessions = engagements.filter((e) => e.status === "active").length;
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [callSession, setCallSession] = useState(null);
  const [stats, setStats] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [sessionFilter, setSessionFilter] = useState("active");
  const [hubTab, setHubTab] = useState(
    initialHubTab === "sessions" ? "sessions" : "offerings"
  );
  const [offeringForm, setOfferingForm] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [egressConfigured, setEgressConfigured] = useState(false);
  const [activeRecording, setActiveRecording] = useState(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const outboxRef = useRef([]);
  const offeringUrlHandled = useRef(false);

  const mutedText = "text-label-secondary";
  const baseText = "text-label-primary";
  const panel = "border-separator-default bg-bg-raised";

  const refreshList = useCallback(async () => {
    if (!mintSpaceId || !appInstanceId) return;
    const res = await listConsultingEngagements(mintSpaceId, appInstanceId);
    if (res.success) setEngagements(res.data.engagements || []);
  }, [mintSpaceId, appInstanceId]);

  useEffect(() => {
    if (!initialEngagementId || selectedId) return;
    setSelectedId(initialEngagementId);
  }, [initialEngagementId, selectedId]);

  useEffect(() => {
    if (initialHubTab === "sessions") setHubTab("sessions");
  }, [initialHubTab]);

  const loadDetail = useCallback(
    async (engagementId) => {
      if (!mintSpaceId || !engagementId) return;
      const res = await getConsultingEngagement(mintSpaceId, engagementId);
      if (res.success) setDetail(res.data);
    },
    [mintSpaceId]
  );

  const loadMessages = useCallback(
    async (engagementId) => {
      if (!mintSpaceId || !engagementId) return;
      const res = await listConsultingMessages(mintSpaceId, engagementId, { limit: 80 });
      if (res.success) setMessages(res.data.messages || []);
    },
    [mintSpaceId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mintSpaceId || !appInstanceId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const listRes = await listConsultingEngagements(mintSpaceId, appInstanceId);
        if (listRes.success) {
          const list = listRes.data.engagements || [];
          setEngagements(list);
          if (!initialEngagementId) {
            if (isAdmin) setHubTab("offerings");
            else setHubTab(list.length > 0 ? "sessions" : "offerings");
          } else {
            setHubTab("sessions");
          }
        }
        if (isAdmin) {
          const st = await getConsultingStats(mintSpaceId, appInstanceId);
          if (st.success) setStats(st.data);
        }
      } catch (err) {
        if (!cancelled) setToast?.(err.message || "Failed to load consulting");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpaceId, appInstanceId, setToast, isAdmin, initialEngagementId]);

  useEffect(() => {
    if (!socket) return undefined;
    const onIncomingCall = (payload) => {
      if (payload?.instanceId && payload.instanceId !== appInstanceId) return;
      if (payload?.mintSpaceId && String(payload.mintSpaceId) !== String(mintSpaceId)) return;
      const name = payload.fromName || t("consulting.someone") || "Someone";
      const tpl = t("consulting.incomingCall") || "{name} is calling you in consulting";
      setToast?.(tpl.replace("{name}", name));
      if (payload.engagementId) setSelectedId(payload.engagementId);
    };
    socket.on("consulting_incoming_call", onIncomingCall);
    return () => socket.off("consulting_incoming_call", onIncomingCall);
  }, [socket, mintSpaceId, appInstanceId, setToast, t]);

  const refreshRecordings = useCallback(async () => {
    if (!mintSpaceId || !selectedId) return;
    try {
      const res = await listConsultingRecordings(mintSpaceId, selectedId);
      if (res.success) {
        setRecordings(res.data.recordings || []);
        setEgressConfigured(!!res.data.egressConfigured);
        const live =
          (res.data.recordings || []).find((r) =>
            ["awaiting_consent", "recording", "processing"].includes(r.status)
          ) || null;
        setActiveRecording(live);
      }
    } catch {
      /* optional */
    }
  }, [mintSpaceId, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setMessages([]);
      setRecordings([]);
      setReplyTarget(null);
      return;
    }
    loadDetail(selectedId);
    loadMessages(selectedId);
    refreshRecordings();
  }, [selectedId, loadDetail, loadMessages, refreshRecordings]);

  useEffect(() => {
    if (!socket || !selectedId) return undefined;
    socket.emit("consulting_join", { engagementId: selectedId });
    const onMsg = (msg) => {
      if (String(msg.engagementId) !== String(selectedId)) return;
      setMessages((prev) => upsertConsultingMessages(prev, msg, userId));
    };
    socket.on("consulting_message", onMsg);
    return () => {
      socket.emit("consulting_leave", { engagementId: selectedId });
      socket.off("consulting_message", onMsg);
    };
  }, [socket, selectedId, userId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const flushOutbox = async () => {
    if (!selectedId || !detail?.canChat) return;
    while (outboxRef.current.length) {
      const text = outboxRef.current.shift();
      try {
        await sendConsultingMessage(mintSpaceId, selectedId, text);
      } catch {
        outboxRef.current.unshift(text);
        break;
      }
    }
  };

  useEffect(() => {
    if (!socket) return undefined;
    const onConnect = () => flushOutbox();
    socket.on("connect", onConnect);
    return () => socket.off("connect", onConnect);
  });

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !selectedId) return;
    const replyToId = replyTarget?._id;
    setInput("");
    setReplyTarget(null);
    const optimistic = {
      _id: `tmp-${Date.now()}`,
      engagementId: selectedId,
      senderId: String(userId),
      senderName: "You",
      type: "text",
      text,
      replyTo: replyTarget
        ? {
            _id: replyTarget._id,
            senderName: replyTarget.senderName,
            text: replyTarget.text,
            type: replyTarget.type || "text",
          }
        : null,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    if (!detail?.canChat) return;
    setSending(true);
    try {
      const res = await sendConsultingMessage(mintSpaceId, selectedId, text, replyToId);
      if (res.success) {
        setMessages((prev) => upsertConsultingMessages(prev, res.data.message, userId));
      }
    } catch (err) {
      outboxRef.current.push(text);
      setToast?.(err.response?.data?.message || err.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const startReply = (message) => {
    if (!message?._id || !detail?.canChat) return;
    const preview =
      message.text?.trim() ||
      (message.type === "image"
        ? "Photo"
        : message.type === "video"
          ? "Video"
          : message.fileName || "Attachment");
    setReplyTarget({
      _id: message._id,
      senderName: message.senderName,
      text: preview,
      type: message.type,
    });
  };

  const startCall = async () => {
    if (!selectedId || !detail?.canCall) return;
    try {
      const res = await getConsultingCallToken(mintSpaceId, selectedId);
      if (!res.success) throw new Error(res.message || "Call failed");
      setCallSession({
        token: res.data.token,
        url: res.data.livekitUrl || LIVEKIT_URL,
      });
      if (res.data.engagement) {
        setEngagements((prev) =>
          prev.map((e) => (e._id === selectedId ? { ...e, ...res.data.engagement } : e))
        );
      }
      await loadDetail(selectedId);
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Could not start call");
    }
  };

  const handlePickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedId || !detail?.canChat) return;
    setUploadingFile(true);
    try {
      const res = await uploadConsultingMedia(mintSpaceId, selectedId, file);
      if (res.success) {
        setMessages((prev) => upsertConsultingMessages(prev, res.data.message, userId));
      }
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleClose = async () => {
    if (!selectedId) return;
    try {
      const res = await closeConsultingEngagement(mintSpaceId, selectedId);
      if (res.success) {
        await refreshList();
        await loadDetail(selectedId);
        setToast?.(t("consulting.accessClosed") || "Access closed");
      }
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Failed to close");
    }
  };

  const filteredEngagements = engagements.filter((e) => {
    if (sessionFilter === "all") return true;
    if (sessionFilter === "archive") return e.status !== "active";
    return e.status === "active";
  });

  const handleDownloadRecording = async (recordingId) => {
    try {
      const blob = await downloadConsultingRecording(mintSpaceId, selectedId, recordingId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "consulting-session.mp4";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Download failed");
    }
  };

  const handlePublishRecording = async (recordingId) => {
    const name =
      window.prompt(t("consulting.publishProductName") || "Product name", "Recorded consultation") ||
      "";
    if (!name.trim()) return;
    const priceStr = window.prompt(t("consulting.publishProductPrice") || "Price (GEL)", "0") || "0";
    try {
      const res = await publishConsultingRecordingProduct(mintSpaceId, selectedId, recordingId, {
        name: name.trim(),
        price: Number(priceStr) || 0,
      });
      if (res.success) {
        setToast?.(t("consulting.publishProductDone") || "Product created");
        await refreshRecordings();
        if (res.data) onMintSpaceSaved?.(res.data);
      }
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Publish failed");
    }
  };

  const openCreateOffering = () => setOfferingForm({});
  const openEditOffering = (productId) => setOfferingForm({ productId: String(productId) });

  useEffect(() => {
    if (offeringUrlHandled.current || !isAdmin || !initialOpenOffering) return;
    offeringUrlHandled.current = true;
    if (initialOpenOffering === "new") {
      setOfferingForm({});
    } else {
      setOfferingForm({ productId: initialOpenOffering });
    }
    setHubTab("offerings");
  }, [initialOpenOffering, isAdmin]);

  const closeOfferingForm = () => {
    setOfferingForm(null);
    onClearOfferingUrl?.();
  };

  const selected = engagements.find((e) => e._id === selectedId);

  const menuItems = [
    isAdmin &&
      onOpenProperties && {
        id: "properties",
        label: t("mintSpace.appProperties") || "Properties",
        icon: FiSettings,
        onClick: onOpenProperties,
      },
    isAdmin && {
      id: "create-offering",
      label: t("consulting.addOffering") || "Add consultation",
      icon: FiDollarSign,
      onClick: openCreateOffering,
    },
  ].filter(Boolean);

  return (
    <MintSpaceAppWindow
      isDark={isDark}
      app="consulting"
      icon={FiMessageSquare}
      iconGradient="from-violet-500 to-indigo-600"
      title={t("mintSpace.appConsultingName") || "Consulting"}
      subtitle={
        t("consulting.subtitleCalendar") ||
        "Pick a time, pay, then meet by video and chat — like a private Zoom session."
      }
      menuItems={menuItems}
      bodyOverflow="hidden"
      bodyClassName="relative flex min-h-0 flex-1 flex-col"
    >
      {loading ? (
        <div className={`flex flex-1 items-center justify-center gap-2 text-subheadline ${mutedText}`}>
          <FiLoader className="h-4 w-4 animate-spin" />
          {t("common.loading") || "Loading…"}
        </div>
      ) : !appInstanceId ? (
        <div className={`flex flex-1 items-center justify-center p-6 text-subheadline ${mutedText}`}>
          {t("consulting.missingInstance") || "Open Consulting from the sidebar."}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <ConsultingUpcomingBanner
            isDark={isDark}
            mintSpaceId={mintSpaceId}
            appInstanceId={appInstanceId}
            onOpenSession={(engagementId) => {
              setSelectedId(engagementId);
              setHubTab("sessions");
            }}
          />
          <div className="mb-space-16 shrink-0">
            <ViewTabs
              label={t("consulting.tabOfferings")}
              value={hubTab}
              onChange={setHubTab}
              selectedClass="bg-app-consulting-deep text-accent-tinted-label shadow-[inset_0_0_0_1px_var(--app-consulting-base)]"
              options={[
                { value: "offerings", label: t("consulting.tabOfferings") },
                {
                  value: "sessions",
                  label: activeSessions > 0 ? `${t("consulting.tabSessions")} · ${activeSessions}` : t("consulting.tabSessions"),
                },
                ...(isAdmin ? [{ value: "calendar", label: t("consulting.tabCalendar") }] : []),
              ]}
            />
          </div>

          {hubTab === "calendar" && isAdmin ? (
            <ConsultingCalendarPanel
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              appInstanceId={appInstanceId}
              isAdmin={isAdmin}
              engagements={engagements}
              onBooked={refreshList}
            />
          ) : hubTab === "offerings" ? (
            <ConsultingOfferingsPanel
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              appInstanceId={appInstanceId}
              isAdmin={isAdmin}
              engagements={engagements}
              onCreateOffering={openCreateOffering}
              onEditOffering={openEditOffering}
              onOpenAffiliate={onOpenAffiliate}
              onOpenSession={(engagementId) => {
                setSelectedId(engagementId);
                setHubTab("sessions");
              }}
              onEngagementsChange={refreshList}
            />
          ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-space-24 min-[900px]:flex-row">
          <aside className="flex w-full shrink-0 flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16 min-[900px]:w-[340px]">
            <h2 className="text-headline text-label-primary">{t("consulting.sessions")}</h2>
            <ViewTabs
              label={t("consulting.sessions")}
              value={sessionFilter}
              onChange={setSessionFilter}
              selectedClass="bg-app-consulting-deep text-accent-tinted-label shadow-[inset_0_0_0_1px_var(--app-consulting-base)]"
              options={["active", "archive", "all"].map((key) => ({ value: key, label: t(`consulting.filter_${key}`) }))}
            />
            {isAdmin && stats && (
              <div className="grid grid-cols-2 gap-space-12">
                <StatCell
                  className="col-span-2"
                  label={t("consulting.statsRevenue")}
                  value={`${stats.totalRevenue?.toFixed?.(2) ?? stats.totalRevenue} ${stats.currency}`}
                />
                <StatCell label={t("consulting.statsActive")} value={stats.activeEngagements} />
                <StatCell label={t("consulting.statsSales")} value={stats.salesCount} />
              </div>
            )}
            <ul className="flex max-h-48 flex-col gap-space-4 overflow-y-auto min-[900px]:max-h-none min-[900px]:flex-1">
              {engagements.length === 0 && (
                <li>
                  <EmptyState
                    icon="user"
                    title={isAdmin ? t("consulting.noSessionsSeller") : t("consulting.noSessionsBuyer")}
                    className="!px-0"
                  />
                </li>
              )}
              {filteredEngagements.map((e) => (
                <li key={e._id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(e._id)}
                    className={`w-full rounded-[22px] px-space-12 py-[10px] text-left transition ${
                      selectedId === e._id ? "bg-app-consulting-deep" : "hover:bg-fill-quaternary"
                    }`}
                  >
                    <p className="truncate text-subheadline-semibold text-label-primary">{e.productName || "Consulting"}</p>
                    <p className="truncate text-caption-1 text-label-secondary">{e.accessLabel}</p>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center">
                <EmptyState icon="user" title={t("consulting.selectSession")} />
              </div>
            ) : (
              <>
                <div
                  className={`flex shrink-0 items-center gap-2 border-b px-3 py-2 border-separator-default`}
                >
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-subheadline-semibold ${baseText}`}>{selected.productName}</p>
                    <p className={`text-caption-1 ${mutedText}`}>{selected.accessLabel}</p>
                    {selected.status === "active" && (
                      <p className={`mt-0.5 text-caption-1 leading-snug ${mutedText}`}>
                        {t("consulting.scheduleInChatHint") ||
                          "Coordinate session times together in this chat — not before payment."}
                      </p>
                    )}
                  </div>
                  {detail?.canCall && (
                    <button
                      type="button"
                      onClick={startCall}
                      className="inline-flex items-center gap-1 rounded-radius-xs bg-accent-default px-2.5 py-1.5 text-footnote-semibold text-accent-on"
                    >
                      <FiPhone className="h-3.5 w-3.5" />
                      {t("consulting.startCall") || "Call"}
                    </button>
                  )}
                  {recordings.length > 0 && (
                    <div
                      className={`border-b px-3 py-2 text-caption-1 border-separator-default`}
                    >
                      <p className={`mb-1 font-semibold ${baseText}`}>
                        {t("consulting.recordings") || "Recordings"}
                      </p>
                      <ul className="space-y-1">
                        {recordings.map((r) => (
                          <li key={r._id} className={`flex flex-wrap items-center gap-2 ${mutedText}`}>
                            <span>{r.status}</span>
                            {r.status === "ready" && (
                              <>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-0.5 underline"
                                  onClick={() => handleDownloadRecording(r._id)}
                                >
                                  <FiDownload className="h-3 w-3" />
                                  {t("consulting.download") || "Download"}
                                </button>
                                {isAdmin && !r.publishedProductId && (
                                  <button
                                    type="button"
                                    className="underline"
                                    onClick={() => handlePublishRecording(r._id)}
                                  >
                                    {t("consulting.publishAsProduct") || "Sell as product"}
                                  </button>
                                )}
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isAdmin && selected.status === "active" && selected.accessMode === "manual" && (
                    <button
                      type="button"
                      onClick={handleClose}
                      className={`rounded-radius-xs border px-2 py-1 text-caption-1 border-separator-default`}
                    >
                      {t("consulting.closeAccess") || "Close access"}
                    </button>
                  )}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
                  {messages.map((m) => {
                    const mine = String(m.senderId) === String(userId);
                    return (
                      <div
                        key={m._id}
                        className={`group mb-2 flex items-end gap-1 ${mine ? "justify-end" : "justify-start"}`}
                      >
                        {!mine && detail?.canChat && (
                          <button
                            type="button"
                            onClick={() => startReply(m)}
                            className={`mb-1 rounded-radius-xs p-1 opacity-0 transition group-hover:opacity-100 ${
                              "hover:bg-fill-quaternary"
                            }`}
                            aria-label={t("consulting.reply") || "Reply"}
                          >
                            <FiCornerUpLeft className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <div
                          className={`max-w-[85%] rounded-radius-md px-3 py-2 text-subheadline ${
                            mine
                              ? "bg-accent-default text-accent-on"
                              : "bg-fill-tertiary text-label-primary"
                          }`}
                        >
                          {!mine && (
                            <p className="mb-0.5 text-caption-1-semibold opacity-70">{m.senderName}</p>
                          )}
                          {m.replyTo && (
                            <div
                              className={`mb-2 border-l-2 pl-2 text-caption-1 ${
                                mine ? "border-ink/30 opacity-80" : "border-app-consulting-base opacity-90"
                              }`}
                            >
                              <p className="font-semibold">{m.replyTo.senderName}</p>
                              <p className="truncate">{m.replyTo.text}</p>
                            </div>
                          )}
                          <ConsultingMessageBody
                            mintSpaceId={mintSpaceId}
                            message={m}
                            isDark={isDark}
                          />
                        </div>
                        {mine && detail?.canChat && (
                          <button
                            type="button"
                            onClick={() => startReply(m)}
                            className={`mb-1 rounded-radius-xs p-1 opacity-0 transition group-hover:opacity-100 ${
                              "hover:bg-fill-quaternary"
                            }`}
                            aria-label={t("consulting.reply") || "Reply"}
                          >
                            <FiCornerUpLeft className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handlePickFile}
                />
                <ConsultingChatComposer
                  isDark={isDark}
                  canChat={!!detail?.canChat}
                  sending={sending}
                  uploadingFile={uploadingFile}
                  input={input}
                  setInput={setInput}
                  onSubmit={handleSend}
                  onAttach={() => fileInputRef.current?.click()}
                  replyTo={replyTarget}
                  onCancelReply={() => setReplyTarget(null)}
                />
              </>
            )}
          </div>
        </div>
          )}
        </div>
      )}

      {offeringForm && isAdmin && (
        <ConsultingOfferingForm
          isDark={isDark}
          setToast={setToast}
          mintSpace={mintSpace}
          appInstanceId={appInstanceId}
          productId={offeringForm.productId}
          onClose={closeOfferingForm}
          onSaved={(data) => {
            onMintSpaceSaved?.(data);
            closeOfferingForm();
          }}
        />
      )}

      {callSession?.token && callSession?.url && (
        <div className="fixed inset-0 z-[9998] flex flex-col bg-black">
          <LiveKitRoom
            token={callSession.token}
            serverUrl={callSession.url}
            connect
            video
            audio
            onDisconnected={() => setCallSession(null)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex shrink-0 items-center justify-between px-3 py-2 text-white">
              <span className="text-subheadline-semibold">{t("consulting.inCall") || "In call"}</span>
              <button type="button" onClick={() => setCallSession(null)} aria-label="Close">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <CallVideoGrid />
            <RoomAudioRenderer />
            <CallRecordingControls
              isDark={isDark}
              mintSpaceId={mintSpaceId}
              engagementId={selectedId}
              recording={activeRecording}
              egressConfigured={egressConfigured}
              onRefresh={refreshRecordings}
              setToast={setToast}
              t={t}
            />
            <CallControls isDark={isDark} onLeave={() => setCallSession(null)} />
          </LiveKitRoom>
        </div>
      )}
    </MintSpaceAppWindow>
  );
}
